import React from "react";
import { useLang } from "@/contexts/LanguageContext";
import { Scale, X } from "lucide-react";

const marginOf = (v) => {
  if (!v.input.purchase_price) return null;
  const b = v.result.strategies.find((s) => s.goal === "balanced");
  return Number(b.price) - Number(v.input.purchase_price);
};

export const CompareSection = ({ compareItems, catLabel, onClose }) => {
  const { t, lang } = useLang();
  const fmtEur = (n) => `€${Number(n).toLocaleString(lang === "it" ? "it-IT" : "en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const bestMarginId = (marginOf(compareItems[0]) ?? -Infinity) >= (marginOf(compareItems[1]) ?? -Infinity) ? compareItems[0].id : compareItems[1].id;

  return (
    <section data-testid="compare-section" className="mt-6 border border-black">
      <div className="flex items-center justify-between px-6 py-4 bg-[#0A0A0A] text-white">
        <span className="overline text-white flex items-center gap-2"><Scale size={14} /> {t.dashboard.compare_title}</span>
        <button data-testid="compare-close" onClick={onClose} className="hover:text-[#FFCC00] transition-colors"><X size={16} /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 md:divide-x divide-black">
        {compareItems.map((v) => {
          const margin = marginOf(v);
          const marginPct = Number(v.input.purchase_price) > 0 ? (margin / Number(v.input.purchase_price)) * 100 : 0;
          const best = bestMarginId === v.id;
          const rows = [
            { l: t.dashboard.purchase, val: v.input.purchase_price ? fmtEur(v.input.purchase_price) : "—" },
            { l: t.results.market_value, val: fmtEur(v.result.estimated_market_value) },
            ...v.result.strategies.map((s) => ({ l: s.label, val: fmtEur(s.price) })),
          ];
          return (
            <div key={v.id} data-testid={`compare-col-${v.id}`} className={`p-6 border-t md:border-t-0 border-black ${best ? "bg-[#FFCC00]/15" : "bg-white"}`}>
              {best && (
                <span data-testid="compare-best-badge" className="inline-block bg-[#FFCC00] text-black font-mono text-[9px] font-bold tracking-widest uppercase px-2 py-1 mb-3">
                  ★ {t.dashboard.best_margin}
                </span>
              )}
              <div className="font-display font-bold text-xl leading-tight">{v.input.brand || "—"} {v.input.model}</div>
              <div className="font-mono text-xs text-[#737373] uppercase mt-1">{catLabel(v.input.category)} · {t.valuator.conditions[v.input.condition] || v.input.condition}</div>
              <div className="mt-5 space-y-2">
                {rows.map((r) => (
                  <div key={r.l} className="flex items-center justify-between border-b border-black/10 pb-2">
                    <span className="overline text-[9px] text-[#737373]">{r.l}</span>
                    <span className="font-mono font-bold text-sm">{r.val}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2">
                  <span className="overline text-[9px]">{t.dashboard.margin}</span>
                  {margin === null ? (
                    <span className="font-mono font-bold text-sm text-[#737373]">—</span>
                  ) : (
                    <span className={`font-display font-black text-xl tracking-tighter ${margin >= 0 ? "text-[#002FA7]" : "text-[#FF3B30]"}`}>
                      {margin >= 0 ? "+" : "−"}{fmtEur(Math.abs(margin))} <span className="font-mono text-xs">({marginPct.toFixed(0)}%)</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default CompareSection;
